import React from 'react';
import { Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

const TestCaseResults = ({ curr_question, curr_submission, test_cases }) => {
  const { t } = useTranslation('practice_tests');
  const parseStatus = tc => {
    if (tc.compiler_error) return 'CE';
    else if (tc.runtime_error) return 'RE';
    else if (tc.timelimitexceeded_error) return 'TLE';
    else {
      if (tc.status === 'SC') return 'AC';
      else if (tc.status === 'EX') return 'LOAD';
      else if (tc.status === 'FL') return 'WA';
      else return 'UA';
    }
  };

  const questionTestCases =
    test_cases || curr_question?.test_cases || curr_question?.testcases || [];
  const testcaseExecutions = curr_submission?.testcase_executions || [];

  const tcMap = {};
  questionTestCases.forEach(tc => {
    tcMap[tc.id] = tc;
  });

  const dataSource = testcaseExecutions.map((execution, idx) => {
    const tc = tcMap[execution.testcase_id] || {};
    const statusCode = parseStatus(execution);

    let statusText = '';
    let statusColor = '#6B7280';
    let statusIcon = '⚠';
    let passed = false;

    switch (statusCode) {
      case 'AC':
        statusText = 'Success';
        statusColor = '#10B981';
        statusIcon = '✓';
        passed = true;
        break;
      case 'WA':
        statusText = 'Failed';
        statusColor = '#EF4444';
        statusIcon = '✗';
        break;
      case 'RE':
        statusText = 'Runtime Error';
        statusColor = '#EF4444';
        statusIcon = '✗';
        break;
      case 'TLE':
        statusText = 'Time Limit Exceeded';
        statusColor = '#F59E0B';
        statusIcon = '⏱';
        break;
      case 'CE':
        statusText = 'Compilation Failed';
        statusColor = '#EF4444';
        statusIcon = '✗';
        break;
      case 'LOAD':
        statusText = 'Loading';
        statusColor = '#6B7280';
        statusIcon = '⟳';
        break;
      case 'UA':
      default:
        statusText = 'Not Attempted';
        statusColor = '#9CA3AF';
        statusIcon = '○';
        break;
    }

    const totalPoints = tc.points || 0;
    const pointsEarned = passed ? totalPoints : 0;

    return {
      key: idx,
      testCase: idx + 1,
      description: tc.description || tc.name || `Test Case ${idx + 1}`,
      difficulty: tc.difficulty || 'EZ',
      points: totalPoints,
      pointsEarned: pointsEarned,
      status: statusText,
      statusColor: statusColor,
      statusIcon: statusIcon,
    };
  });

  const totalPoints = dataSource.reduce((sum, tc) => sum + tc.points, 0);
  const earnedPoints = dataSource.reduce((sum, tc) => sum + tc.pointsEarned, 0);

  const columns = [
    {
      title: 'Test Case No',
      dataIndex: 'testCase',
      key: 'testCase',
      width: 150,
      render: text => <span style={{ fontSize: '14px', color: '#111827' }}>{text}</span>,
    },
    {
      title: 'Difficulty',
      dataIndex: 'difficulty',
      key: 'difficulty',
      width: 150,
      render: difficulty => {
        const difficultyMap = {
          EZ: 'Easy',
          MD: 'Medium',
          HD: 'Hard',
        };
        const label = difficultyMap[difficulty] || 'Easy';
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              background: '#F3F4F6',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#374151',
              fontWeight: '500',
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      width: 120,
      render: points => (
        <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{points}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: text => <span style={{ fontSize: '14px', color: '#111827' }}>{text}</span>,
    },
  ];

  return (
    <div
      data-test-id="test-case-results"
      data-specific-id="submission-test-cases"
    >
      <h4
        style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}
        data-test-id="test-cases-title"
        data-specific-id="test-cases-heading"
      >
        {t('practice_tests.test_result.test_cases')}
      </h4>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        size="middle"
        bordered={false}
        style={{
          background: '#FFFFFF',
        }}
        rowClassName={() => 'test-case-row'}
        data-test-id="test-cases-table"
        data-specific-id="submission-test-cases-table"
        footer={() => (
          <div
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827',
              padding: '8px 0',
            }}
            data-test-id="total-points-footer"
            data-specific-id="test-cases-total-points"
          >
            {t('practice_tests.test_result.total_points', {
              earned: earnedPoints,
              total: totalPoints,
            })}
          </div>
        )}
      />
    </div>
  );
};

export default TestCaseResults;
